import React from 'react';
import { Button, Table } from 'react-bootstrap';
import {
  ArrowCounterclockwise,
  ArrowRight,
  BoxArrowUpRight,
  Trash,
} from 'react-bootstrap-icons';

import { GetInvoicePaymentLinkDtoTypeEnum } from '../../../common/spdCore/autogenerated/spdApiClient';
import CurrencyControl from '../../../app/components/CurrencyControl';
import mathUtils from '../../../utils/mathUtils';
import numberFormatter from '../../../common/numberFormatter';

import { PendingLink } from './InvoicePaymentLinksTypes';

interface LinksTableProps {
  links: PendingLink[];
  paymentAmount?: string | null;
  readOnly?: boolean;
  onAmountChange: (key: string, value: string) => void;
  onCopyUnlinked: (key: string) => void;
  onDelete: (key: string) => void;
  onNavigate: (salesInvoiceId: string) => void;
}

interface LinkRowCellsProps {
  link: PendingLink;
  readOnly?: boolean;
  onCopyUnlinked: (key: string) => void;
  onNavigate: (salesInvoiceId: string) => void;
}

const LinkRowCells: React.FC<LinkRowCellsProps> = ({
  link,
  readOnly,
  onCopyUnlinked,
  onNavigate,
}) => {
  if (link.type === GetInvoicePaymentLinkDtoTypeEnum.Withdrawal) {
    return (
      <>
        <td colSpan={5} className="fst-italic text-secondary">
          Wyprowadzono poza system
          {link.comment && <span>{` (${link.comment})`}</span>}
        </td>
        <td></td>
      </>
    );
  }
  return (
    <>
      <td>
        {link.systemNumber ?? '—'}
        {link.salesInvoiceId && !link.toDelete && (
          <>
            {' '}
            <BoxArrowUpRight
              className="ms-1 mb-1 icon-clickable icon-primary"
              title="Otwórz fakturę w nowym oknie"
              onClick={() => onNavigate(link.salesInvoiceId)}
            />
          </>
        )}
      </td>
      <td>{link.number ?? '—'}</td>
      <td>{link.contractorName ?? '—'}</td>
      <td className="amounts-align-right">
        {numberFormatter(link.amountGross, true) ?? '—'}
      </td>
      <td className="amounts-align-right">
        {numberFormatter(link.unlinkedAmount || '0', true) ?? '—'}
      </td>
      <td className="link-copy-col">
        {!readOnly && !link.toDelete && (
          <Button
            variant="outline-secondary"
            size="sm"
            title="Kopiuj kwotę niedowiązaną faktury"
            onClick={() => onCopyUnlinked(link.key)}
            className="link-copy-btn"
          >
            <ArrowRight />
          </Button>
        )}
      </td>
    </>
  );
};

export const LinksTable: React.FC<LinksTableProps> = ({
  links,
  paymentAmount,
  readOnly,
  onAmountChange,
  onCopyUnlinked,
  onDelete,
  onNavigate,
}) => {
  const visible = links.filter(l => !l.toDelete);
  const totalLinked = mathUtils.add(...visible.map(l => l.amount || '0'));
  const payment = mathUtils.parse(paymentAmount ?? '0');
  const remaining = paymentAmount
    ? mathUtils.subtract(payment, totalLinked)
    : 0;

  return (
    <Table striped hover responsive size="sm">
      <thead>
        <tr>
          <th>Nr sys.</th>
          <th>Nr faktury</th>
          <th>Kontrahent</th>
          <th>Kwota do dowiązania</th>
          <th className="amounts-align-right">Pozostało do dowiązania</th>
          <th className="link-copy-col"></th>
          <th className="amounts-align-right">Kwota powiązania</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        {links.map(link => (
          <tr
            key={link.key}
            className={
              link.toDelete ? 'text-decoration-line-through text-muted' : ''
            }
          >
            <LinkRowCells
              link={link}
              readOnly={readOnly}
              onCopyUnlinked={onCopyUnlinked}
              onNavigate={onNavigate}
            />
            <td className="amounts-align-right">
              {link.toDelete ||
              link.type === GetInvoicePaymentLinkDtoTypeEnum.Withdrawal ||
              link.type === GetInvoicePaymentLinkDtoTypeEnum.Correction ||
              readOnly ? (
                numberFormatter(link.amount, true)
              ) : (
                <CurrencyControl
                  value={link.amount}
                  decimalScale={2}
                  onChange={v => onAmountChange(link.key, v || '')}
                />
              )}
            </td>
            <td>
              {!readOnly &&
                link.type !== GetInvoicePaymentLinkDtoTypeEnum.Correction &&
                (link.toDelete ? (
                  <ArrowCounterclockwise
                    className="icon-clickable icon-danger"
                    title="Przywróć"
                    onClick={() => onDelete(link.key)}
                  />
                ) : (
                  <Trash
                    className="icon-clickable icon-danger"
                    title="Usuń"
                    onClick={() => onDelete(link.key)}
                  />
                ))}
            </td>
          </tr>
        ))}
      </tbody>
      {paymentAmount && (
        <tfoot className="table-group-divider">
          <tr className="fw-semibold">
            <td colSpan={6} className="text-end">
              Kwota wpłaty:
            </td>
            <td>{numberFormatter(payment, true)}</td>
            <td></td>
          </tr>
          <tr className="fw-semibold">
            <td colSpan={6} className="text-end">
              Łącznie powiązane:
            </td>
            <td>{numberFormatter(totalLinked || '0', true)}</td>
            <td></td>
          </tr>
          <tr className="fw-semibold table-warning">
            <td colSpan={6} className="text-end">
              Pozostało do dowiązania:
            </td>
            <td>{numberFormatter(remaining ?? 0, true) || '0,00 zł'}</td>
            <td></td>
          </tr>
        </tfoot>
      )}
    </Table>
  );
};
